// Plain-SVG charts for the patient detail page. Each one carries a visually
// hidden table with the same numbers for screen readers.
import React from "react";
import { formatScore, niceMax, weekLabel } from "./careUtils";

// Score (left axis) and level (right axis) per week for one game.
// weeks: [{ week_start, score|null, level|null }]
export const LineChart = ({ weeks, title = "Average score and level by week" }) => {
  const W = 720;
  const H = 250;
  const l = 48;
  const r = 40;
  const t = 14;
  const b = 32;
  const iw = W - l - r;
  const ih = H - t - b;

  const scores = weeks.filter((w) => w.score !== null).map((w) => w.score);
  const levels = weeks.filter((w) => w.level !== null).map((w) => w.level);
  const maxS = niceMax(Math.max(0, ...scores) * 1.15);
  const maxL = (levels.length ? Math.max(...levels) : 1) + 2;
  const x = (i) => l + (iw * i) / Math.max(1, weeks.length - 1);
  const ys = (v) => t + ih * (1 - v / maxS);
  const yl = (v) => t + ih * (1 - v / maxL);

  const grid = [0, 1, 2, 3, 4].map((k) => {
    const y = t + (ih * k) / 4;
    return (
      <g key={`g${k}`}>
        <line className="grid" x1={l} x2={W - r} y1={y} y2={y} />
        <text x={l - 8} y={y + 4} textAnchor="end">
          {formatScore(Math.round(maxS * (1 - k / 4) * 10) / 10)}
        </text>
        <text className="axis-level" x={W - r + 8} y={y + 4}>
          {Math.round(maxL * (1 - k / 4))}
        </text>
      </g>
    );
  });

  // Level is a step line that starts at the first week with a known level.
  let step = "";
  weeks.forEach((w, i) => {
    if (w.level === null) return;
    step += step ? ` H${x(i)} V${yl(w.level)}` : `M${x(i)} ${yl(w.level)}`;
  });
  if (step) step += ` H${x(weeks.length - 1)}`;

  // Score line breaks wherever a week had no completed session.
  let scorePath = "";
  let pen = false;
  weeks.forEach((w, i) => {
    if (w.score === null) {
      pen = false;
      return;
    }
    scorePath += `${pen ? " L" : " M"}${x(i)} ${ys(w.score)}`;
    pen = true;
  });

  // Label the start of each quiet stretch after the first scored week.
  const firstScored = weeks.findIndex((w) => w.score !== null);
  const gaps = [];
  weeks.forEach((w, i) => {
    if (firstScored > -1 && i > firstScored && w.score === null && weeks[i - 1].score !== null) {
      gaps.push(i);
    }
  });

  return (
    <figure style={{ margin: 0 }}>
      <svg
        className="ct-chart"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={title}
      >
        {grid}
        {weeks.map((w, i) =>
          i % 2 === 1 || i === weeks.length - 1 ? (
            <text key={`x${i}`} x={x(i)} y={H - 8} textAnchor="middle">
              {weekLabel(w.week_start)}
            </text>
          ) : null,
        )}
        {step && <path className="level-line" d={step} />}
        {scorePath && <path className="score-line" d={scorePath} />}
        {weeks.map((w, i) =>
          w.score === null ? null : (
            <circle key={`c${i}`} className="score-point" cx={x(i)} cy={ys(w.score)} r={4}>
              <title>
                {`${weekLabel(w.week_start)}: score ${formatScore(w.score)}${
                  w.level !== null ? `, level ${w.level}` : ""
                }`}
              </title>
            </circle>
          ),
        )}
        {gaps.map((i) => (
          <text key={`gap${i}`} className="no-play" x={x(i)} y={t + 14} textAnchor="middle">
            no play
          </text>
        ))}
      </svg>
      <div className="ct-sr-only">
        <table>
          <caption>{title}</caption>
          <thead>
            <tr>
              <th scope="col">Week of</th>
              <th scope="col">Average score</th>
              <th scope="col">Level</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((w) => (
              <tr key={w.week_start}>
                <td>{weekLabel(w.week_start)}</td>
                <td>{w.score === null ? "No play" : formatScore(w.score)}</td>
                <td>{w.level === null ? "—" : w.level}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
};

// Sessions per week, all games. weeks: [{ week_start, count }]; the last
// entry is the current week.
export const BarChart = ({ weeks, title = "Sessions per week" }) => {
  const W = 520;
  const H = 220;
  const l = 30;
  const r = 6;
  const t = 22;
  const b = 30;
  const iw = W - l - r;
  const ih = H - t - b;
  const values = weeks.map((w) => w.count);
  const max = Math.max(7, ...values);
  const bw = iw / Math.max(1, weeks.length);

  return (
    <figure style={{ margin: 0 }}>
      <svg
        className="ct-chart"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={title}
      >
        <line className="baseline" x1={l} x2={W - r} y1={t + ih} y2={t + ih} />
        {weeks.map((w, i) => {
          const v = w.count;
          const bh = (ih * v) / max;
          const bx = l + i * bw + bw * 0.2;
          const last = i === weeks.length - 1;
          return (
            <g key={w.week_start}>
              {v > 0 && (
                <rect
                  className={`bar${last ? " bar--current" : ""}`}
                  x={bx}
                  y={t + ih - bh}
                  width={bw * 0.6}
                  height={bh}
                  rx={2}
                />
              )}
              <text
                className={v ? "bar-value" : "bar-value--zero"}
                x={bx + bw * 0.3}
                y={t + ih - bh - 6}
                textAnchor="middle"
              >
                {v}
              </text>
              <text x={bx + bw * 0.3} y={H - 8} textAnchor="middle">
                {last ? "This wk" : weekLabel(w.week_start)}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="ct-sr-only">
        <table>
          <caption>{title}</caption>
          <thead>
            <tr>
              <th scope="col">Week of</th>
              <th scope="col">Sessions</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((w, i) => (
              <tr key={w.week_start}>
                <td>{i === weeks.length - 1 ? "This week" : weekLabel(w.week_start)}</td>
                <td>{w.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
};
